import { Link, SxProps, Typography, TypographyProps } from '@mui/material';
import { deepmerge } from '@mui/utils';
import { AnchorHTMLAttributes, ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { Resources, Translation } from '../../../@types/i18next';

/**
 * Supported translation tags: if a label is marked with any of these tags or derivations of it,
 * the inner text will be rendered with the corresponding style
 *
 * Examples
 * - <bold>some text</bold>
 * - <anchor_1>link 1</anchor_1> and then <anchor_2>link 2</anchor_2>
 * - <color_red>important text</color_red> and <color_black>less important</color_black>
 */
type Key = 'anchor' | 'docs' | 'bold' | 'italic' | 'color' | 'br' | 'code' | 'a';

/**
 * <anchor> interpolations support all attributes of a <a> tag
 */
type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & {};

/**
 * <docs> interpolations are links whose target is always `_blank`
 */
type DocsProps = LinkProps;

/**
 * <color> interpolations must receive the color they will be rendered with
 */
type ColorProps = { color: string };

/**
 * Type-checking for valid interpolations:
 * - { name: 'color_1', color: 'my.color' } is accepted
 * - { name: 'not_supported' } is a TS error
 */
type Name<T extends Key> = `${T}_${string}` | T;

/**
 * <bold>, <italic>, <br/>, <code> and <a> items are handled by default, no additional prop should be provided.
 * Only configurable interpolations are exported.
 */
export type InterpolationItem = ({ name: Name<'anchor'> } & LinkProps) | ({ name: Name<'docs'> } & DocsProps) | ({ name: Name<'color'> } & ColorProps);

type InterpolationWithLabelProps = {
  i18nKey: Translation<Resources>;
  items?: InterpolationItem[];
  values?: Record<string, string | number | null>;
  children?: undefined;
};

type InterpolationWithContentProps = {
  i18nKey?: undefined;
  items?: undefined;
  values?: undefined;
  children: ReactNode;
};

export type InterpolationProps = TypographyProps & (InterpolationWithLabelProps | InterpolationWithContentProps);

const STYLES = {
  wordBreak: 'break-word',
  '& a[role=button]': { cursor: 'pointer' },
  '& .color': { fontSize: 'inherit' },
  '& .bold': { fontSize: 'inherit', fontWeight: 500 },
  '& .italic': { fontSize: 'inherit', fontStyle: 'italic' },
  '& .br': { display: 'block', height: '1rem', content: '""' },
  '& .code': {
    fontSize: '90%',
    letterSpacing: '-0.25px',
    fontFamily: 'monospace',
    display: 'inline-block',
    backgroundColor: 'aliceblue',
    px: 0.5,
    borderRadius: '4px',
  },
} satisfies SxProps;

const LinkComponent = (props: LinkProps) => {
  return <Link {...props} />;
};

const DocsComponent = (props: LinkProps) => {
  return <LinkComponent {...props} target="_blank" />;
};

export const Interpolation = ({ i18nKey, values = {}, items = [], children, sx, ...rest }: InterpolationProps) => {
  const components = [...items, { name: 'bold' }, { name: 'italic' }, { name: 'br' }, { name: 'code' }, { name: 'a' }]
    .map((i) => {
      const { name, ...rest } = i;
      if (name === 'bold') {
        return { name, component: <Typography component="span" className="bold" /> };
      } else if (name === 'italic') {
        return { name, component: <Typography component="span" className="italic" /> };
      } else if (name === 'code') {
        return { name, component: <Typography component="code" className="code" /> };
      } else if (name === 'br') {
        return { name, component: <Typography component="span" className="br" /> };
      } else if (name === 'a') {
        // Support for backend templates: the <a> tag is passed with `href` and `target` attributes,
        // nothing else is required for rendering
        return { name, component: <LinkComponent /> };
      } else if (name.startsWith('color')) {
        return { name, component: <Typography component="span" className="color" sx={{ ...rest }} /> };
      } else if (name.startsWith('anchor')) {
        return { name, component: <LinkComponent {...rest} /> };
      } else if (name.startsWith('docs')) {
        return { name, component: <DocsComponent {...rest} /> };
      } else {
        // Type-checking makes this case unreachable, but `map` must return something
        return { name, component: <Typography component="span" /> };
      }
    })
    .reduce((prev, curr) => ({ ...prev, [curr.name]: curr.component }), {});

  return (
    <Typography component="span" {...rest} sx={deepmerge(STYLES, sx)}>
      <Trans i18nKey={i18nKey as string} values={values} components={components}>
        {children}
      </Trans>
    </Typography>
  );
};
